'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Explore', href: '/explore' },
  { label: 'Categories', href: '/categories' },
  { label: 'Start Here', href: '/start-here' },
  { label: 'About', href: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'py-3' : 'py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div
            className="nav-pill flex items-center justify-between px-4 sm:px-6 py-3 rounded-full"
            style={{
              boxShadow: scrolled ? '0 4px 20px rgba(24,49,83,0.10)' : 'none',
            }}
          >
            {/* Logo */}
            <Link href="/" className="flex items-center group" aria-label="Drawn2Security home">
              {/*
                The wordmark is roughly 3:1. AppLogo forced it into a square,
                which is why it read as a smudge at small sizes. Fixed height,
                automatic width.
              */}
              <Image
                src="/logo-wordmark.png"
                alt="Drawn2Security"
                width={2071}
                height={659}
                priority
                className="h-8 w-auto sm:h-9"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground/70 hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-muted transition-all duration-200"
                aria-label="Search"
              >
                <Icon name="MagnifyingGlassIcon" size={18} variant="outline" />
              </button>
              <Link
                href="/explore"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-all duration-200"
              >
                Browse all
                <Icon name="ArrowRightIcon" size={14} variant="outline" />
              </Link>
              {/* Mobile menu button */}
              <button
                onClick={() => setMenuOpen(true)}
                className="md:hidden p-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-muted transition-all"
                aria-label="Open menu"
              >
                <Icon name="Bars3Icon" size={20} variant="outline" />
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="mt-2 animate-fade-in">
              <form
                onSubmit={(e) => {
                  e?.preventDefault();
                  if (searchQuery?.trim()) {
                    window.location.href = `/explore?q=${encodeURIComponent(searchQuery)}`;
                  }
                }}
                className="nav-pill flex items-center gap-3 px-5 py-3 rounded-2xl"
              >
                <Icon name="MagnifyingGlassIcon" size={18} variant="outline" className="text-muted-foreground flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search ideas, concepts, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="flex-1 bg-transparent text-foreground text-sm outline-none placeholder:text-muted-foreground"
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="p-1 rounded-full text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="XMarkIcon" size={16} variant="outline" />
                </button>
              </form>
            </div>
          )}
        </div>
      </header>
      {/* Mobile menu overlay */}
      {menuOpen && (
        <>
          <div
            className="mobile-menu-overlay animate-fade-in"
            onClick={() => setMenuOpen(false)}
          />
          <div className="mobile-menu-panel animate-fade-in">
            <div className="flex items-center justify-between">
              <Link href="/" className="flex items-center" aria-label="Drawn2Security home">
                <Image
                  src="/logo-wordmark.png"
                  alt="Drawn2Security"
                  width={2071}
                  height={659}
                  className="h-7 w-auto"
                />
              </Link>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-muted transition-all"
                aria-label="Close menu"
              >
                <Icon name="XMarkIcon" size={20} variant="outline" />
              </button>
            </div>

            <nav className="flex flex-col gap-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-all ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>

            <div className="mt-auto pt-6 border-t border-border">
              <Link
                href="/explore"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary text-primary-foreground rounded-xl font-medium text-base"
              >
                Browse all sketches
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}